import { Button, Divider, Flex, Image, message, Space, Tag } from 'antd';
import './index.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { isMobileAtom } from '@/jotai/atoms/userAtom';
import {
  examinePictureFrame,
  getPictureFrameDetail,
  PictureFrameDetail,
  PictureFrameExamineStatus,
  PictureFrameType,
} from '@/api/pictureFrame';
import { useQuery } from '@tanstack/react-query';
import RejectDialog from './rejectDialog';
import ConfirmDialog from './confirmDialog';

interface FrameDetailItemProps {
  id: string;
  examineCallback: () => void;
}

const FrameDetailItem = ({ id, examineCallback }: FrameDetailItemProps) => {
  const navigate = useNavigate();
  const isMobile = useAtomValue(isMobileAtom);
  const [messageApi, contextHolder] = message.useMessage();

  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState<boolean>(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] =
    useState<boolean>(false);

  const labelStyle: React.CSSProperties = {
    fontWeight: 'bold',
    padding: isMobile ? 0 : '0 10px',
  };

  const { data: pictureFrameDetail } = useQuery({
    queryKey: ['pictureFrameDetail', id],
    queryFn: async () => {
      const resp = await getPictureFrameDetail(Number(id));
      if (resp.status !== 200) {
        return [];
      }
      return resp.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const handleRejectConfirm = async (remark: string) => {
    const resp = await examinePictureFrame({
      id: Number(id),
      examineStatus: PictureFrameExamineStatus.REJECTED,
      examineRefuseNotes: remark,
    });
    if (resp.status !== 200) {
      messageApi.error('审核失败');
      return;
    }
    messageApi.success('审核成功');
    setIsRejectDialogOpen(false);
    examineCallback();
  };

  const handleConfirmConfirm = async (remark?: string) => {
    const resp = await examinePictureFrame({
      id: Number(id),
      examineStatus: PictureFrameExamineStatus.APPROVED,
      examineRefuseNotes: remark || '',
    });
    if (resp.status !== 200) {
      messageApi.error('审核失败');
      return;
    }
    messageApi.success('审核成功');
    setIsConfirmDialogOpen(false);
    examineCallback();
  };

  const renderFileSize = (size: string) => {
    return `${(Number(size) / 1024 / 1024).toFixed(2)}MB`;
  };

  const renderStatus = (status: PictureFrameExamineStatus) => {
    switch (status) {
      case PictureFrameExamineStatus.APPROVED:
        return <Tag color="success">通过</Tag>;
      case PictureFrameExamineStatus.REJECTED:
        return <Tag color="error">不通过</Tag>;
      case PictureFrameExamineStatus.PENDING:
        return <Tag color="default">待审核</Tag>;
      case PictureFrameExamineStatus.EXPIRED:
        return <Tag color="default">已过期</Tag>;
      default:
        return <Tag color="default">未知</Tag>;
    }
  };

  const renderFooter = (
    frameStatus: PictureFrameExamineStatus,
    status: PictureFrameType,
    detail: PictureFrameDetail
  ) => {
    const pictureFrameDetailLength = pictureFrameDetail?.length;
    if (
      frameStatus === PictureFrameExamineStatus.EXPIRED ||
      status === PictureFrameType.DELETE ||
      frameStatus === PictureFrameExamineStatus.PENDING ||
      (pictureFrameDetailLength && pictureFrameDetailLength > 1)
    ) {
      return null;
    }
    return (
      <>
        <Divider />
        <Flex style={{ marginTop: 10, justifyContent: 'space-between' }}>
          <Flex style={{ flexDirection: 'column' }} gap={10}>
            <Flex
              style={{ flexDirection: isMobile ? 'column' : 'row' }}
              gap={10}
            >
              <div>审核人</div>
              <div>{detail.examineUserId}</div>
            </Flex>
            <Flex
              style={{ flexDirection: isMobile ? 'column' : 'row' }}
              gap={10}
            >
              <div>审核状态</div>
              <div>{renderStatus(detail.examineStatus)}</div>
            </Flex>
          </Flex>
          <Flex style={{ flexDirection: 'column' }} gap={10}>
            <Flex
              style={{ flexDirection: isMobile ? 'column' : 'row' }}
              gap={10}
            >
              <div>审核时间</div>
              <div>{detail.examineTime}</div>
            </Flex>
            <Flex
              style={{ flexDirection: isMobile ? 'column' : 'row' }}
              gap={10}
            >
              <div>审核原因</div>
              <div>{detail.examineRefuseNotes || '-'}</div>
            </Flex>
          </Flex>
        </Flex>
      </>
    );
  };

  const renderActionBtns = () => {
    const isFinished = pictureFrameDetail?.every(
      (item) => item.examineStatus !== PictureFrameExamineStatus.PENDING
    );
    const isExpired = pictureFrameDetail?.find(
      (item) => item.examineStatus === PictureFrameExamineStatus.EXPIRED
    );
    const isDelete = pictureFrameDetail?.find(
      (item) => item.frameType === PictureFrameType.DELETE
    );
    if (isFinished || isExpired || isDelete) {
      return null;
    }

    return (
      <>
        <Divider />
        <Space>
          <Button type="primary" onClick={() => setIsConfirmDialogOpen(true)}>
            通过
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => setIsRejectDialogOpen(true)}
          >
            不通过
          </Button>
          <Button onClick={() => navigate('/picture-frame')}>取消</Button>
        </Space>
      </>
    );
  };

  return (
    <div>
      {contextHolder}
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
        }}
      >
        <div style={labelStyle}>画框审核ID</div>
        <div>{id || '-'}</div>
      </Flex>
      <Flex
        style={{
          marginBottom: 10,
          flexDirection: isMobile ? 'column' : 'row',
          gap: 20,
        }}
      >
        {pictureFrameDetail?.map((item, index) => {
          return (
            <div key={index} style={{ flexGrow: 1 }}>
              {pictureFrameDetail?.length > 1 && (
                <div
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <div style={labelStyle}>
                    {index === 0 ? '上次发布' : '本次发布'}
                  </div>
                </div>
              )}
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Image src={item.frameUrl} width={isMobile ? 100 : 150} />
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>画框名称</div>
                <div>{item.frameName}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>画框价格</div>
                <div>{item.price}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>已售卖数</div>
                <div>{item.salesQuantity}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>获得积分</div>
                <div>-</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>文件大小</div>
                <div>{renderFileSize(item.fileSize)}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>文件类型</div>
                <div>{item.fileType}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>文件分辨率</div>
                <div>{`${item.width}px * ${item.height}px`}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>USER ID</div>
                <div>{item.userId}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>SUPP ID</div>
                <div>{item.suppId || '后端未返回'}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>类型</div>
                <div>
                  {item.publicationType === 0 ? '首次发布' : '编辑发布'}
                </div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>提交时间</div>
                <div>{item.examineTime}</div>
              </Flex>
              <Flex
                style={{
                  marginBottom: 10,
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <div style={labelStyle}>状态</div>
                <div>{renderStatus(item.examineStatus)}</div>
              </Flex>

              <div>
                {renderFooter(item.examineStatus, item.frameType, item)}
              </div>
            </div>
          );
        })}
      </Flex>
      <div style={{ textAlign: 'center' }}>{renderActionBtns()}</div>

      <RejectDialog
        isOpen={isRejectDialogOpen}
        onClose={() => setIsRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
      />

      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => setIsConfirmDialogOpen(false)}
        onConfirm={handleConfirmConfirm}
      />
    </div>
  );
};

const FrameDetailPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [currentId, setCurrentId] = useState<string>('');
  const idsRef = useRef<string[]>([]);

  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',') || [];
    idsRef.current = ids;
    const index = parseInt(searchParams.get('index') || '0');
    if (ids.length) {
      setCurrentIndex(index);
      setCurrentId(ids[index]);
    }
  }, [searchParams]);

  const handlePrev = useCallback(() => {
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex);
    const params = new URLSearchParams(searchParams);
    params.set('index', newIndex.toString());
    setSearchParams(params);
    const prevId = idsRef.current[newIndex];
    if (prevId) {
      setCurrentId(prevId);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  const handleNext = useCallback(() => {
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex);
    const params = new URLSearchParams(searchParams);
    params.set('index', newIndex.toString());
    setSearchParams(params);
    const nextId = idsRef.current[newIndex];
    if (nextId) {
      setCurrentId(nextId);
    }
  }, [currentIndex, searchParams, setSearchParams]);

  return (
    <div className="picture-frame-detail-container">
      <div className="pagination-btn-container">
        <Button
          type="primary"
          shape="circle"
          className="pagination-btn-left"
          onClick={handlePrev}
          disabled={currentIndex === 0}
        >
          &lt;
        </Button>
      </div>

      <div className="frame-item-container">
        <FrameDetailItem id={currentId} examineCallback={handleNext} />
      </div>

      <div className="pagination-btn-container">
        <Button
          type="primary"
          shape="circle"
          className="pagination-btn-right"
          onClick={handleNext}
          disabled={currentIndex === idsRef.current.length - 1}
        >
          &gt;
        </Button>
      </div>
    </div>
  );
};

export default FrameDetailPage;
